/**
 * Format a UTC ISO timestamp for display in the viewer's browser timezone.
 * Replaces scattered `toLocaleString('en-IN', ...)` and `date-fns` format() calls.
 */
export function formatTimestamp(
  iso: string | Date | null | undefined,
  opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string {
  if (!iso) return '—';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return new Intl.DateTimeFormat(undefined, { ...opts, timeZone: tz }).format(new Date(iso));
}

/**
 * Format a YYYY-MM-DD calendar-day string (stored in IST) for display.
 * Constructs a local Date from parts to avoid UTC-midnight shifting — a viewer in
 * UTC-5 would otherwise see "Jan 14" for a date stored as "2024-01-15".
 */
export function formatDate(
  isoDate: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string {
  if (!isoDate) return '—';
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Intl.DateTimeFormat(undefined, opts).format(new Date(y, m - 1, d));
}

/**
 * Parse a YYYY-MM-DD string as a local-midnight Date for comparisons (e.g. overdue checks).
 * Avoids `new Date(isoDate)` which treats the string as UTC midnight and shifts the day
 * for viewers in negative-offset timezones.
 */
export function parseLocalDate(isoDate: string): Date {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, m - 1, d);
}
